import { DownOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Checkbox, Modal, notification, Tag } from 'antd';
import { updateDestinationTravelThemes } from 'api/destinations.api';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import type { DestinationTravelThemeSchema } from 'typings/destinations/DestinationTravelThemeSchema';
import type { TravelThemeSchema } from 'typings/travel-theme/TravelThemeSchema';
import styles from './DestinationTravelThemeSelector.module.css';

const MAX_THEMES = 3;

type DestinationTravelThemeSelectorProps = Readonly<{
    destinationId: number;
    allThemes: TravelThemeSchema[];
    travelThemes: DestinationTravelThemeSchema[];
    onUpdate: (themes: DestinationTravelThemeSchema[]) => void;
}>;

function toSortedIds(themes: DestinationTravelThemeSchema[]): number[] {
    return [...(Array.isArray(themes) ? themes : [])]
        .sort((a, b) => a.position - b.position)
        .map((dt) => dt.travel_theme_id);
}

export function DestinationTravelThemeSelector({
    destinationId,
    allThemes,
    travelThemes,
    onUpdate,
}: DestinationTravelThemeSelectorProps) {
    const [open, setOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);

    const currentIds = useMemo(() => toSortedIds(travelThemes), [travelThemes]);

    const handleOpen = useCallback(() => {
        setSelectedIds(currentIds);
        setOpen(true);
    }, [currentIds]);

    const handleSave = useCallback(async () => {
        const payload = selectedIds.map((id, i) => ({
            travel_theme_id: id,
            position: i,
        }));
        setSaving(true);
        try {
            const result = await updateDestinationTravelThemes(
                destinationId,
                payload,
            );
            const themes = Array.isArray(result)
                ? result
                : buildDestinationTravelThemesFromIds(
                      selectedIds,
                      destinationId,
                      allThemes,
                  );
            onUpdate(themes);
            setOpen(false);
        } catch {
            notification.error({ message: 'Failed to update themes' });
        } finally {
            setSaving(false);
        }
    }, [selectedIds, destinationId, allThemes, onUpdate]);

    const handleToggle = useCallback((themeId: number) => {
        setSelectedIds((prev) => {
            if (prev.includes(themeId))
                return prev.filter((id) => id !== themeId);
            if (prev.length >= MAX_THEMES) return prev;
            return [...prev, themeId];
        });
    }, []);

    const handleMove = useCallback((themeId: number, direction: -1 | 1) => {
        setSelectedIds((prev) => {
            const index = prev.indexOf(themeId);
            const nextIndex = index + direction;
            if (index < 0 || nextIndex < 0 || nextIndex >= prev.length)
                return prev;
            const next = [...prev];
            [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
            return next;
        });
    }, []);

    const displayThemes = useMemo(() => {
        const themeById = new Map(allThemes.map((t) => [t.id, t]));
        return currentIds
            .map((id) => themeById.get(id))
            .filter((theme): theme is TravelThemeSchema => theme !== undefined);
    }, [allThemes, currentIds]);

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const selectedThemes = useMemo(() => {
        const themeById = new Map(allThemes.map((t) => [t.id, t]));
        return selectedIds
            .map((id) => themeById.get(id))
            .filter((theme): theme is TravelThemeSchema => theme !== undefined);
    }, [allThemes, selectedIds]);

    const unselectedThemes = useMemo(
        () =>
            allThemes
                .filter((t) => !selectedIdSet.has(t.id))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [allThemes, selectedIdSet],
    );

    const canReorder = selectedThemes.length > 1;
    const isFull = selectedIds.length >= MAX_THEMES;

    return (
        <>
            <div className={styles.selector}>
                {displayThemes.length > 0 ? (
                    <button
                        type="button"
                        className={styles.selector__edit}
                        aria-label="Edit travel themes"
                        onClick={handleOpen}
                    >
                        {displayThemes.map((theme) => (
                            <Tag
                                key={theme.id}
                                className={styles.selector__tag}
                            >
                                {theme.icon_path && (
                                    <img
                                        src={theme.icon_path}
                                        alt=""
                                        width={16}
                                        height={16}
                                        className={styles.selector__tagIcon}
                                    />
                                )}
                                {theme.name}
                            </Tag>
                        ))}
                    </button>
                ) : (
                    <Button
                        type="text"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={handleOpen}
                    >
                        Add themes
                    </Button>
                )}
            </div>
            <Modal
                title="Travel Themes"
                open={open}
                onCancel={() => setOpen(false)}
                onOk={handleSave}
                okText="Save"
                confirmLoading={saving}
                width={400}
                destroyOnClose
            >
                <ul className={styles.modal}>
                    {selectedThemes.map((theme, index) => (
                        <TravelThemeRow
                            key={theme.id}
                            theme={theme}
                            isActive
                            isDisabled={false}
                            showReorder={canReorder}
                            index={index}
                            total={selectedThemes.length}
                            onToggle={handleToggle}
                            onMove={handleMove}
                        />
                    ))}

                    {selectedThemes.length > 0 &&
                    unselectedThemes.length > 0 ? (
                        <li className={styles.modal__divider} aria-hidden />
                    ) : null}

                    {unselectedThemes.map((theme) => (
                        <TravelThemeRow
                            key={theme.id}
                            theme={theme}
                            isActive={false}
                            isDisabled={isFull}
                            showReorder={false}
                            index={0}
                            total={0}
                            onToggle={handleToggle}
                            onMove={handleMove}
                        />
                    ))}
                    <li className={styles.modal__counter}>
                        {selectedIds.length}/{MAX_THEMES}
                    </li>
                </ul>
            </Modal>
        </>
    );
}

type TravelThemeRowProps = Readonly<{
    theme: TravelThemeSchema;
    isActive: boolean;
    isDisabled: boolean;
    showReorder: boolean;
    index: number;
    total: number;
    onToggle: (themeId: number) => void;
    onMove: (themeId: number, direction: -1 | 1) => void;
}>;

function TravelThemeRow({
    theme,
    isActive,
    isDisabled,
    showReorder,
    index,
    total,
    onToggle,
    onMove,
}: TravelThemeRowProps) {
    return (
        <li className={styles.modal__itemRow}>
            <button
                type="button"
                role="option"
                aria-selected={isActive}
                aria-disabled={isDisabled}
                disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                className={classNames(styles.modal__item, {
                    [styles.disabled]: isDisabled,
                })}
                onClick={() => !isDisabled && onToggle(theme.id)}
                onKeyDown={(e) => {
                    if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onToggle(theme.id);
                    }
                }}
            >
                <Checkbox
                    checked={isActive}
                    tabIndex={-1}
                    style={{ pointerEvents: 'none' }}
                />
                {theme.icon_path ? (
                    <img
                        src={theme.icon_path}
                        alt=""
                        className={styles.modal__itemIcon}
                    />
                ) : (
                    <span className={styles.modal__itemIcon} />
                )}
                <span className={styles.modal__itemName}>{theme.name}</span>
            </button>
            {showReorder ? (
                <span className={styles.modal__itemActions}>
                    <button
                        type="button"
                        className={styles.modal__itemActionButton}
                        aria-label={`Move ${theme.name} up`}
                        disabled={index === 0}
                        onClick={() => onMove(theme.id, -1)}
                    >
                        <UpOutlined />
                    </button>
                    <button
                        type="button"
                        className={styles.modal__itemActionButton}
                        aria-label={`Move ${theme.name} down`}
                        disabled={index === total - 1}
                        onClick={() => onMove(theme.id, 1)}
                    >
                        <DownOutlined />
                    </button>
                </span>
            ) : null}
        </li>
    );
}

function buildDestinationTravelThemesFromIds(
    selectedIds: number[],
    destinationId: number,
    allThemes: TravelThemeSchema[],
): DestinationTravelThemeSchema[] {
    return selectedIds
        .map((travel_theme_id, position) => {
            const travel_theme = allThemes.find(
                (t) => t.id === travel_theme_id,
            );

            if (!travel_theme) return null;

            return {
                id: 0,
                destination_id: destinationId,
                travel_theme_id,
                position,
                travel_theme,
            };
        })
        .filter(
            (travelTheme): travelTheme is DestinationTravelThemeSchema =>
                travelTheme !== null,
        );
}
